import { Extension } from '@tiptap/core';
import { NodeSelection, Plugin, PluginKey } from '@tiptap/pm/state';
import type { EditorState } from '@tiptap/pm/state';
import type { Node as ProseMirrorNode } from '@tiptap/pm/model';
import type { EditorView } from '@tiptap/pm/view';
import { Decoration, DecorationSet } from '@tiptap/pm/view';

import { SIGNATURE_NODE_NAME } from './signatureNode';
import { SIGNATURE_DRAG_MIME } from './signatureDrop';
import { pairSignature, signaturePairTarget } from './signaturePairing';
import type { PairTarget } from './signaturePairing';

/**
 * Shows which anchor a drag is about to land beside, and on which side of it.
 *
 * Not decoration for its own sake: the same gesture has two outcomes — pair with this stamp, or
 * go above or below it — and nothing else on screen distinguishes them. Without this the author
 * finds out which one they got by letting go.
 *
 * The highlight is drawn only where a pair would ACTUALLY form. It asks the same
 * `pairSignature` the drop asks, so a row whose other cell is taken, or an anchor inside a list
 * item, shows nothing rather than promising something the drop then refuses.
 */
export const SIGNATURE_PAIR_ATTRIBUTE = 'data-pair-side';

/** The class on the line showing where a relocated anchor will land. */
export const SIGNATURE_DROP_LINE_CLASS = 'ds-signature-drop-line';

/**
 * What a drag is about to do: pair with an anchor, or land at a position between blocks.
 *
 * Two shapes because they are two different promises. Pairing puts the stamp beside one that is
 * already there; an insert puts it on a line of its own, and the author cannot tell which from
 * the stamp following their pointer.
 */
export type DropHint = { kind: 'pair'; target: PairTarget } | { kind: 'insert'; pos: number };

const pluginKey = new PluginKey<DropHint | null>('signaturePairHighlight');

/**
 * Tells the highlight what the current gesture would do.
 *
 * Exported because an anchor already in the document is moved with POINTER events, not HTML5
 * drag-and-drop — there are no `dragover` events to watch, so the gesture reports its own
 * target. The palette's drag still arrives as a real drag and is watched below.
 *
 * Either anchor kind; only `pair` is signature-specific.
 */
export const setAnchorDropHint = (view: EditorView, hint: DropHint | null): void => {
	if (sameHint(pluginKey.getState(view.state) ?? null, hint)) {
		return;
	}
	view.dispatch(view.state.tr.setMeta(pluginKey, hint));
};

/** The node a drag is carrying, when it is one this can pair. */
const draggedSignature = (
	view: EditorView,
	event: DragEvent
): { node: ProseMirrorNode; source?: { from: number; to: number } } | null => {
	// A palette drag. `getData` is unreadable until the drop, so the TYPE is what identifies it
	// — and the slot's identity makes no difference to whether a pair can form, so a bare node
	// of the right type answers the question.
	if (event.dataTransfer?.types.includes(SIGNATURE_DRAG_MIME) === true) {
		const type = view.state.schema.nodes[SIGNATURE_NODE_NAME];
		return type === undefined
			? null
			: {
					node: type.create({
						signingRoleId: 'preview',
						isWitness: false,
						signatureIndex: 0,
					}),
				};
	}
	if (view.dragging === null) {
		return null;
	}
	const { selection } = view.state;
	return selection instanceof NodeSelection && selection.node.type.name === SIGNATURE_NODE_NAME
		? { node: selection.node, source: { from: selection.from, to: selection.to } }
		: null;
};

const targetUnder = (view: EditorView, event: DragEvent): PairTarget | null => {
	const dragged = draggedSignature(view, event);
	if (dragged === null) {
		return null;
	}
	const target = signaturePairTarget(view, event);
	if (target === null) {
		return null;
	}
	return pairSignature(view.state, target, dragged.node, dragged.source) === null ? null : target;
};

const sameHint = (a: DropHint | null, b: DropHint | null): boolean => {
	if (a === null || b === null) {
		return a === b;
	}
	if (a.kind === 'pair' && b.kind === 'pair') {
		return a.target.pos === b.target.pos && a.target.side === b.target.side;
	}
	return a.kind === 'insert' && b.kind === 'insert' && a.pos === b.pos;
};

/** The line drawn where a relocated anchor will land. Styled in `flowDocumentEditor.styled`. */
const dropLine = (): HTMLElement => {
	const line = document.createElement('div');
	line.className = SIGNATURE_DROP_LINE_CLASS;
	return line;
};

export const SignaturePairHighlight = Extension.create({
	name: 'signaturePairHighlight',

	addProseMirrorPlugins() {
		return [
			new Plugin<DropHint | null>({
				key: pluginKey,
				state: {
					init: () => null,
					apply: (transaction, value) => {
						const meta = transaction.getMeta(pluginKey);
						if (meta !== undefined) {
							return meta satisfies DropHint | null;
						}
						// Any edit invalidates the position the highlight is pinned to, and a
						// stale one decorates whatever moved into that spot.
						return transaction.docChanged ? null : value;
					},
				},
				props: {
					decorations: (state: EditorState) => {
						const hint = pluginKey.getState(state) ?? null;
						if (hint === null) {
							return null;
						}
						if (hint.kind === 'insert') {
							return DecorationSet.create(state.doc, [
								Decoration.widget(hint.pos, dropLine, { side: -1 }),
							]);
						}
						const node = state.doc.nodeAt(hint.target.pos);
						if (node === null) {
							return null;
						}
						return DecorationSet.create(state.doc, [
							Decoration.node(hint.target.pos, hint.target.pos + node.nodeSize, {
								[SIGNATURE_PAIR_ATTRIBUTE]: hint.target.side,
							}),
						]);
					},
					handleDOMEvents: {
						// Every handler returns false: this only ever watches. The drop itself
						// belongs to `handleDrop`, and stopping a dragover would suppress the
						// drop entirely.
						dragover: (view, event) => {
							if (event instanceof DragEvent) {
								const target = targetUnder(view, event);
								setAnchorDropHint(
									view,
									target === null ? null : { kind: 'pair', target }
								);
							}
							return false;
						},
						dragleave: (view) => {
							setAnchorDropHint(view, null);
							return false;
						},
						dragend: (view) => {
							setAnchorDropHint(view, null);
							return false;
						},
						drop: (view) => {
							setAnchorDropHint(view, null);
							return false;
						},
					},
				},
			}),
		];
	},
});
